import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CLUB_LOGO } from "@/lib/api";
import { Calendar, HelpCircle, Users, FileText, Phone, Mail, ArrowRight, Trophy, MapPin, Clock, Shield, Target, Heart, UserPlus, TrendingUp, Zap } from "lucide-react";

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  } catch (e) {
    return d;
  }
}

function formatShortDate(d) {
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [results, setResults] = useState([]);

  useEffect(() => {
    api.get("/events/upcoming").then((r) => setEvents(r.data.slice(0, 4))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/events/recent-results?limit=4").then((r) => setResults(r.data)).catch(() => {});
  }, []);

  const nextMatch = events.find((e) => e.type === "match");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A]">
        <div
          className="absolute inset-0 opacity-65"
          style={{
            backgroundImage:
              "url(https://customer-assets-agu9un31.emergentagent.net/job_club-infos/artifacts/57f6e9b38ab0f628_IMG_1773.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/70 to-[#0F172A]/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3 animate-rise">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-[#D97706]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Site officiel du club</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              Tout le club<br />
              <span className="text-[#D97706]">au bout des doigts</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-xl leading-relaxed">
              Calendrier, actualités, FAQ, documents : toutes les infos du club à portée de clic.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/calendrier" data-testid="hero-cta-calendar" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-[#1E3A8A] font-semibold hover:bg-[#D97706] hover:text-white transition-colors duration-200">
                <Calendar size={18} /> Voir le calendrier
              </Link>
              <Link to="/faq" data-testid="hero-cta-faq" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors duration-200">
                <HelpCircle size={18} /> Consulter la FAQ
              </Link>
            </div>
          </div>
          <div className="hidden lg:flex lg:col-span-2 justify-center">
            <div className="relative">
              <div className="absolute -inset-6 bg-[#D97706]/20 rounded-full blur-3xl" />
              <img src={CLUB_LOGO} alt="Logo" className="relative h-72 w-72 object-contain drop-shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CLUB VALUES / IDENTITY */}
      <section className="relative bg-[#0F172A] text-white overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 bg-[#1E3A8A]/40 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 bg-[#D97706]/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2">
              <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Villeurbanne United FC</span>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter leading-[1.05]" style={{ fontFamily: "Outfit" }}>
                Un club,<br />
                <span className="text-[#D97706]">trois valeurs</span>.
              </h2>
              <p className="mt-5 text-slate-300 leading-relaxed">
                Fondé en 2017 au cœur de Villeurbanne, notre club forme les jeunes passionnés dans l'exigence, le respect et l'esprit d'équipe.
              </p>
              <div className="mt-6 space-y-1 text-sm">
                <div className="flex items-center gap-2 text-slate-200"><MapPin size={14} className="text-[#D97706]" /> 171 rue Anatole France, 69100 Villeurbanne</div>
                <div className="flex items-center gap-2 text-slate-200"><Clock size={14} className="text-[#D97706]" /> Secrétariat : 18h – 20h</div>
                <div className="flex items-center gap-2 text-slate-200"><Phone size={14} className="text-[#D97706]" /> 06 32 47 51 74 (Président)</div>
                <a href="mailto:presidencevufc@yahoo.com" className="flex items-center gap-2 text-slate-200 hover:text-[#D97706] transition-colors break-all"><Mail size={14} className="text-[#D97706]" /> presidencevufc@yahoo.com</a>
              </div>
            </div>
            <div className="lg:col-span-3 grid sm:grid-cols-3 gap-4">
              {[
                { icon: Shield, title: "Fierté", desc: "Porter nos couleurs avec honneur, à chaque entraînement et à chaque match." },
                { icon: Target, title: "Rigueur", desc: "Travailler sérieusement, être ponctuel et donner le meilleur de soi-même." },
                { icon: Heart, title: "Collectif", desc: "Un pour tous. Le respect des coéquipiers, du staff et de l'adversaire d'abord." },
              ].map((v) => {
                const Icon = v.icon;
                return (
                  <div key={v.title} className="p-6 rounded-lg border border-white/10 bg-white/5 backdrop-blur hover:border-[#D97706]/60 transition-colors">
                    <Icon className="h-7 w-7 text-[#D97706] mb-4" />
                    <div className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{v.title}</div>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{v.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* QUICK ACTIONS - BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Accès rapide</span>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
              Ce que vous cherchez, direct.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 lg:gap-4">
          {[
            { to: "/inscriptions", icon: UserPlus, label: "Inscriptions", desc: "Rejoindre le club", accent: true },
            { to: "/calendrier", icon: Calendar, label: "Calendrier", desc: "Matchs & entraînements" },
            { to: "/live-matchs", icon: Zap, label: "Live Matchs", desc: "Scores & vidéos du week-end" },
            { to: "/faq", icon: HelpCircle, label: "FAQ", desc: "Vos questions" },
            { to: "/documents", icon: FileText, label: "Documents", desc: "À télécharger" },
          ].map((a, i) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.to}
                to={a.to}
                data-testid={`quick-action-${a.label.toLowerCase()}`}
                className={`group relative overflow-hidden rounded-lg border p-5 lg:p-6 hover:-translate-y-1 transition-all duration-200 ${a.accent ? "border-[#D97706] bg-[#FEF3C7]/40 hover:border-[#D97706]" : "border-[#E2E8F0] bg-white hover:border-[#1E3A8A]"}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <Icon className={`h-7 w-7 mb-4 ${a.accent ? "text-[#D97706]" : "text-[#1E3A8A]"}`} />
                <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{a.label}</div>
                <div className="text-xs text-[#475569] mt-1">{a.desc}</div>
                <ArrowRight className={`absolute top-5 right-5 h-4 w-4 ${a.accent ? "text-[#D97706]/40" : "text-[#CBD5E1]"} group-hover:text-[#D97706] group-hover:translate-x-1 transition-all`} />
              </Link>
            );
          })}
        </div>
      </section>

      {/* NEXT MATCH + UPCOMING */}
      <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <span className="text-xs tracking-[0.3em] uppercase text-[#DC2626] font-bold">Prochain match</span>
            {nextMatch ? (
              <div className="mt-3 bg-white border border-[#E2E8F0] rounded-lg p-6 lg:p-8" data-testid="next-match-card">
                <div className="flex items-center gap-2 text-[#DC2626] font-bold">
                  <Trophy size={16} /> {nextMatch.category}
                </div>
                <h3 className="mt-3 text-xl lg:text-2xl font-bold text-[#0F172A] leading-tight" style={{ fontFamily: "Outfit" }}>
                  {nextMatch.title}
                </h3>
                <div className="mt-5 space-y-2 text-sm text-[#475569]">
                  <div className="flex items-center gap-2"><Calendar size={14} /> {formatDate(nextMatch.date)}</div>
                  <div className="flex items-center gap-2"><Clock size={14} /> {nextMatch.time}</div>
                  <div className="flex items-center gap-2"><MapPin size={14} /> {nextMatch.location}</div>
                </div>
                {nextMatch.notes && (
                  <div className="mt-5 pt-5 border-t border-[#E2E8F0] text-sm text-[#475569]">{nextMatch.notes}</div>
                )}
              </div>
            ) : (
              <div className="mt-3 bg-white border border-[#E2E8F0] rounded-lg p-6 text-sm text-[#475569]">
                Aucun match programmé pour l'instant.
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-4">
              <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">À venir</span>
              <Link to="/calendrier" className="text-sm font-semibold text-[#1E3A8A] hover:text-[#D97706] transition-colors flex items-center gap-1" data-testid="see-all-events">
                Tout voir <ArrowRight size={14} />
              </Link>
            </div>
            <div className="space-y-2">
              {events.length === 0 && (
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 text-sm text-[#475569]">Rien de prévu pour l'instant.</div>
              )}
              {events.map((e) => (
                <div key={e.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between gap-4 hover:border-[#1E3A8A] transition-colors" data-testid={`event-row-${e.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`shrink-0 rounded-md w-14 h-14 flex flex-col items-center justify-center text-xs font-bold ${e.type === "match" ? "bg-[#DC2626] text-white" : "bg-[#1E3A8A] text-white"}`}>
                      <div className="text-[10px] uppercase tracking-wider opacity-80">{e.type === "match" ? "Match" : "Entraîn."}</div>
                      <div>{new Date(e.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</div>
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A] truncate" style={{ fontFamily: "Outfit" }}>{e.title}</div>
                      <div className="text-xs text-[#475569] mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Clock size={12} /> {e.time}</span>
                        <span className="flex items-center gap-1"><MapPin size={12} /> {e.location}</span>
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-semibold">{e.category}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RECENT RESULTS */}
      {results.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-testid="recent-results">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-[#DC2626] font-bold">Derniers résultats</span>
              <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
                Résultats de nos équipes
              </h2>
            </div>
            <Link to="/calendrier" className="text-sm font-semibold text-[#1E3A8A] hover:text-[#D97706] transition-colors flex items-center gap-1" data-testid="see-all-results">
              Voir tout <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {results.map((r) => {
              const vufcHome = r.venue_type === "domicile";
              const vScore = parseInt(r.vufc_score || "0", 10);
              const oScore = parseInt(r.opp_score || "0", 10);
              const won = vScore > oScore;
              const lost = vScore < oScore;
              const bg = won ? "bg-green-50 border-green-200" : lost ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200";
              const tag = won ? { label: "V", color: "bg-green-600" } : lost ? { label: "D", color: "bg-red-600" } : { label: "N", color: "bg-slate-500" };
              return (
                <div key={r.id} className={`rounded-lg border p-5 ${bg}`} data-testid={`result-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 bg-white rounded border border-slate-200 text-[#0F172A]">{r.category}</span>
                    <span className={`w-6 h-6 ${tag.color} text-white rounded text-[11px] font-bold flex items-center justify-center`}>{tag.label}</span>
                  </div>
                  <div className="text-xs text-[#475569]">{formatShortDate(r.date)}</div>
                  <div className="mt-3 flex items-center justify-center gap-3 text-center">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-[#475569] truncate">{vufcHome ? "VUFC" : r.opponent}</div>
                    </div>
                    <div className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
                      {vufcHome ? `${vScore} – ${oScore}` : `${oScore} – ${vScore}`}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-[#475569] truncate">{vufcHome ? r.opponent : "VUFC"}</div>
                    </div>
                  </div>
                  {r.championship && <div className="mt-3 text-[10px] text-[#475569] italic truncate text-center">{r.championship}</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* NEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Dernières actualités</span>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
              La vie du club
            </h2>
          </div>
          <Link to="/actualites" className="text-sm font-semibold text-[#1E3A8A] hover:text-[#D97706] transition-colors flex items-center gap-1" data-testid="see-all-news">
            Toutes les actus <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {news.map((n) => (
            <Link key={n.id} to={`/actualites/${n.id}`} data-testid={`news-card-${n.id}`} className="group border border-[#E2E8F0] rounded-lg overflow-hidden hover:-translate-y-1 hover:border-[#1E3A8A] transition-all duration-200 bg-white">
              <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                {n.image_url ? (
                  <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] flex items-center justify-center">
                    <img src={CLUB_LOGO} alt="VU" className="h-24 w-24 object-contain opacity-80" />
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#D97706] font-bold">{new Date(n.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}</div>
                <h3 className="mt-2 font-bold text-[#0F172A] group-hover:text-[#1E3A8A] transition-colors" style={{ fontFamily: "Outfit" }}>{n.title}</h3>
                <p className="mt-2 text-sm text-[#475569] line-clamp-2">{n.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA bottom */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="relative overflow-hidden rounded-xl border border-[#E2E8F0] bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#1E3A8A] p-8 lg:p-14">
          <div className="absolute -top-10 -right-10 h-48 w-48 bg-[#D97706]/30 rounded-full blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Besoin d'une info ?</span>
              <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "Outfit" }}>
                Avant d'appeler, consultez la FAQ.
              </h2>
              <p className="mt-3 text-slate-200 max-w-md">
                90% des questions ont déjà leur réponse dans notre FAQ. Rapide, précis, à toute heure.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/faq" data-testid="cta-faq-bottom" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-[#1E3A8A] font-semibold hover:bg-[#D97706] hover:text-white transition-colors">
                <HelpCircle size={18} /> Ouvrir la FAQ
              </Link>
              <Link to="/contact" data-testid="cta-contact-bottom" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors">
                <Phone size={18} /> Formulaire
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
